/*
 * This mixin adds computed properties that read off the module's this.asset
 */

import { ResizeSensor } from 'css-element-queries';
import { get, throttle, sortBy } from 'lodash-es';
import { mapState, mapActions } from 'pinia';
import {
  MP4_2MB_FORMAT_ID,
  MP4_300K_FORMAT_ID,
  M3U8_PLAYLIST_FORMAT_ID,
  VTT_FORMAT_ID,
  GIF_JPG_PNG_GENERAL_IMAGE,
  SMALL_IMAGES,
  MEDIUM_IMAGES,
  LARGE_IMAGES,
  MAP_IMAGE_BASE,
  MAP_IMAGE_OVERLAY,
} from '@/lib/constants/mediaFileFormatIds';
import * as appConstants from '@/lib/constants/app';
import {
  assetStatusDescriptions,
  TEI_ASSET_SUBTYPES,
} from '@/lib/constants';
import { bus } from '@/lib/eventBus';
import { getEnv } from '@/lib/api';
import {
  useEditorStore,
  useAssetStore,
} from '@/stores';
import * as types from '@/lib/constants/store';

export default {
  data() {
    return {
      moduleWidth: 0,
      resizeSensor: null,
      throttledUpdateModuleWidth() {}, // see note in mounted()
    };
  },
  methods: {
    ...mapActions(useEditorStore, [
      types.CACHE_URL_REQUEST,
    ]),
    ...mapActions(useAssetStore, [
      types.GET_ASSET_PLAYER_LINK,
    ]),
  },
  computed: {
    ...mapState(useEditorStore, [
      'domains',
    ]),
    assetVideoMediaFiles() {
      if (!this.asset || !this.asset.media_files) return [];

      const sources = [];

      if (!this.asset.is_user_created) {
        // The first video source for DE videos should always be an m3u8 playlist.
        // Azure videos already have a url for this, but other videos get a
        // playlist from an edde api endpoint
        const m3u8Playlist = this.asset.media_files.find(
          (mediaFile) => mediaFile.format_id === M3U8_PLAYLIST_FORMAT_ID && mediaFile.status !== 10,
        );

        sources.push(m3u8Playlist || {
          format: {
            id: M3U8_PLAYLIST_FORMAT_ID,
          },
          id: `${this.asset.id}-m3u8-playlist`, // this is only used as a key in the embedded player
          url: `${this.domains.deDomain}/videos/${this.asset.id}.m3u8?use_cdn=true`,
        });
      }

      // The other two video sources are HD and SD mp4s
      const mp4HD = this.asset.media_files.find(
        (mediaFile) => mediaFile.format_id === MP4_2MB_FORMAT_ID,
      );
      if (mp4HD) sources.push(mp4HD);

      const mp4SD = this.asset.media_files.find(
        (mediaFile) => mediaFile.format_id === MP4_300K_FORMAT_ID,
      );
      if (mp4SD) sources.push(mp4SD);

      return sources;
    },
    assetClosedCaptions() {
      if (!this.asset || !this.asset.media_files) return [];

      return this.asset.media_files.filter((mediaFile) => (
        mediaFile.format_id === VTT_FORMAT_ID
      )).map((cc) => {
        const parsedCC = {
          src_lang: 'en',
          label: 'en',
          url: cc.url,
        };

        // For a valid language code, the filename should be of the format
        // <asset_guid>_<language_code>-<language_region>.vtt or <asset_guid>-<language_code>-<language_region>.vtt
        // If it is in any other format, assume it is English.
        const fileName = cc.url.split('/').slice(-1)[0];
        const fileNamePieces = fileName.match(/^[a-f\d]{8}-(?:[a-f\d]{4}-){3}[a-f\d]{12}[-_]([^-]+)-(.+)\.vtt$/);
        if (fileNamePieces) {
          parsedCC.src_lang = fileNamePieces[1]; // eslint-disable-line
          parsedCC.label = fileNamePieces[1]; // eslint-disable-line
        }

        return parsedCC;
      }).sort((a, b) => {
        // Pull English captions to the beginning
        if (a.src_lang === 'en') return -1;
        if (b.src_lang === 'en') return 1;
        return 0;
      });
    },
    assetThumbnailUrl() {
      if (!this.asset) return null;
      const assetId = this.asset.id;

      const mediaFiles = get(this, 'asset.media_files', []);

      if (this.asset.is_user_created) {
        const validMediaFile = mediaFiles.find((mediaFile) => mediaFile.format_id !== -1);

        if (validMediaFile) {
          return {
            thumbnailLoaderId: assetId,
            imageUrls: [{ url: validMediaFile.url, isThumbnail: false }],
          };
        }

        return {};
      }

      let imageUrls = [];
      let largeImageUrls = [];

      const thumbSmall = { isThumbnail: true, url: `//d1m0scxbrw6oep.cloudfront.net/thumbnails/${this.asset.id}-small` };
      const thumbMedium = { isThumbnail: true, url: `//d1m0scxbrw6oep.cloudfront.net/thumbnails/${this.asset.id}-medium` };
      const thumbLarge = { isThumbnail: true, url: `//d1m0scxbrw6oep.cloudfront.net/thumbnails/${this.asset.id}-large` };

      const getMediaFileUrls = (formatIds) => mediaFiles
        .filter((file) => file.url && formatIds.includes(file.format_id))
        .map((file) => ({ isThumbnail: false, url: file.url }));

      const mediaUrl = get(this, 'asset.media_files[0].open_url') || get(this, 'asset.media_files[0].url');
      const gifRegex = /\.gif(\?|$)/i; // Matches .gif at the end of a string or followed by a ?
      const isGifUrl = gifRegex.test(mediaUrl);

      const mediaFileSmall = getMediaFileUrls([SMALL_IMAGES]);
      const mediaFileMedium = getMediaFileUrls([MEDIUM_IMAGES]);
      const mediaFileLarge = getMediaFileUrls([LARGE_IMAGES]);
      const mediaFileGeneral = getMediaFileUrls([GIF_JPG_PNG_GENERAL_IMAGE]);
      const mediaFileOther = getMediaFileUrls([MAP_IMAGE_BASE, MAP_IMAGE_OVERLAY]);

      // The zoom modal will always use the largest images available, so
      // we need to keep track of those regardless
      largeImageUrls.push(thumbLarge);
      largeImageUrls.push(...mediaFileLarge);
      largeImageUrls.push(thumbMedium);
      largeImageUrls.push(...mediaFileMedium);
      largeImageUrls.push(thumbSmall);
      largeImageUrls.push(...mediaFileSmall);

      // The imageUrls array is what's used for responsive images
      if (this.moduleWidth > 540) {
        imageUrls.push(...largeImageUrls);
      } else if (this.moduleWidth < 360) {
        imageUrls.push(thumbSmall);
        imageUrls.push(...mediaFileSmall);
        imageUrls.push(thumbMedium);
        imageUrls.push(...mediaFileMedium);
        imageUrls.push(thumbLarge);
        imageUrls.push(...mediaFileLarge);
      } else {
        imageUrls.push(thumbMedium);
        imageUrls.push(...mediaFileMedium);
        imageUrls.push(thumbLarge);
        imageUrls.push(...mediaFileLarge);
        imageUrls.push(thumbSmall);
        imageUrls.push(...mediaFileSmall);
      }

      imageUrls.push(...mediaFileGeneral);
      imageUrls.push(...mediaFileOther);
      largeImageUrls.push(...mediaFileGeneral);
      largeImageUrls.push(...mediaFileOther);

      if (isGifUrl) {
        // If the media file is a gif, we want to make sure it's the first image in the array
        // The false isThumbnail value ensures gif/mediaFiles appear first in the array
        // This places them ahead of thumbnails
        imageUrls = sortBy(imageUrls, 'isThumbnail');
        largeImageUrls = sortBy(largeImageUrls, 'isThumbnail');
      }

      return {
        thumbnailLoaderId: assetId,
        imageUrls,
        largeImageUrls,
      };
    },
    playerLink() {
      if (!this.asset) {
        return '';
      }

      const assetPlayerLink = this[types.GET_ASSET_PLAYER_LINK]();
      if (assetPlayerLink) {
        return `${assetPlayerLink}${this.asset.id}`;
      }

      return `${this.$appDomain}/player/${this.asset.id}`;
    },
    usePlayer() {
      if (!this.asset) {
        return false;
      }

      const assetSubtype = this.asset?.subtype?.name || this.asset?.sub_type?.name;
      if (TEI_ASSET_SUBTYPES.includes(assetSubtype) && this.isFullSlideAsset) {
        // Full-slide TEIs render instead of using the Nebula card view
        return false;
      }

      // Player link is only available for DE assets
      return this.playerLink;
    },
    viewInContentUrl() {
      const assetId = get(this, 'asset.id');
      if (!assetId) return null;

      // Studio content is authored in the US site rather than in a region-specific content site
      const appDomainUS = appConstants[`VUE_APP_${getEnv()}_APPURL_US`];

      // Create a url like https://atlas.discoveryeducation.com/assets/<ASSET_ID>
      return appDomainUS.replace('app', 'content').replace('learn', `assets/${assetId}`);
    },
    assetStatus() {
      return this.asset?.status?.id;
    },
    assetStatusDescription() {
      if (!this.assetStatus) return null;
      return assetStatusDescriptions()[this.assetStatus] || this.$t('Inactive');
    },
  },
  watch: {
    scale() {
      if (this.throttledUpdateModuleWidth) this.throttledUpdateModuleWidth();
    },
  },
  mounted() {
    const vm = this;

    this.$nextTick(() => {
      if (!this.$el || this.ignoreResize) return;

      this.moduleWidth = this.$el.getBoundingClientRect().width;
      this.resizeSensor = new ResizeSensor(
        this.$el,
        throttle(({ height, width }) => {
          vm.moduleWidth = width;

          /**
           * Asset modules can sometimes get caught in a loop when the contents are just a
           * little taller than the module itself. The browser tries to add a scrollbar,
           * then sees that the contents got shorter since the thumbnail shrank, and removes
           * the scrollbar, then sees that the contents are too tall and adds it again, etc.,
           * causing the module to flicker. To prevent that, when the contents and the module
           * are close to the same size, we force scrollbars to appear.
           */
          const moduleContents = vm.$el.querySelector('.asset-module--vertical');

          if (moduleContents) {
            const moduleContentsHeight = Math.round(moduleContents.getBoundingClientRect().height);
            const scrollParent = vm.$el.closest('.component-module-wrapper__module-contents');
            const moduleHasScrollbars = scrollParent
              && scrollParent.offsetWidth - vm.$el.offsetWidth > 1;

            bus.emit('module:force-scrollbars', {
              moduleId: this.id,
              // Scrollbars should appear if the contents are taller than the module:
              forceScrollbars: moduleContentsHeight > height
                || (
                  // OR if the module already has a scrollbar but the contents are
                  // just a bit short:
                  moduleContentsHeight > (height - 16 * this.scale) && moduleHasScrollbars
                ),
            });
          }
        }, 500),
      );

      // Adding a throttle() as a method means that in practice _all_ components using this mixin
      // will get throttled together (so the function may run for some components and not for
      // others). Setting it here instead means that there will be a different throttle() for each
      // instance, which is what we want.
      this.throttledUpdateModuleWidth = throttle(() => {
        vm.moduleWidth = vm.$el.getBoundingClientRect().width;
      }, 500);
    });
  },
  beforeUnmount() {
    if (this.$el && this.resizeSensor) ResizeSensor.detach(this.$el);
    this.resizeSensor = null;
  },
};
